import { useMutation } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useCallback, useRef, useState } from 'react';

import { ROUTES } from '@/constants/routes';
import { SUPPORT_EMAIL_LINK } from '@/constants/support';
import { useAbility } from '@/hooks/useAbility';
import { useEmailLink } from '@/hooks/useEmailLink';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useToaster } from '@/hooks/useToaster';
import type { ConversationError } from '@/services/conversations/errors';
import { trackSupportContactStarted } from '@/util/analyticsEvents';
import { navigateIntoTabs } from '@/util/navigateIntoTabs';

import { getMySupportThread } from '../api/getMySupportThread';
import type { ContactSupportRequest } from '../model/supportContact';
import { setSupportDraft } from '../state/supportDraftStore';

type UseContactSupportOptions = {
  /**
   * The caller is a screen stacked over the tabs, such as Account, where a push
   * would stack a second tab navigator. The thread opens inside the tabs instead.
   */
  overTabs?: boolean;
  /**
   * Where the member goes when the server refuses them a thread, such as the
   * support account itself or an account whose confirmation lapsed. Defaults to
   * email, so a refusal never strands them.
   */
  onRefused?: () => void;
};

/**
 * Sends a signed-in member to their own support thread with Primum, in place
 * of asking them to email support.
 *
 * `canUseThread` is false for guests and for accounts that have not finished
 * confirmation, which the server refuses a thread; those callers keep their
 * support form or email link. A refusal the client could not predict (the
 * support account asking for its own thread, a lapsed confirmation, no support
 * account configured) goes to `onRefused` rather than a toast, since retrying
 * cannot help. Only a failure with no response is worth trying again.
 *
 * One resolution runs at a time, so a double tap opens the thread once. A
 * draft is handed to the thread through a one-shot store, never sent.
 */
export function useContactSupport({ overTabs = false, onRefused }: UseContactSupportOptions = {}) {
  const ability = useAbility();

  const isCompactLayout = useIsCompactLayout();

  const toast = useToaster();

  const { openEmailLink } = useEmailLink();

  const canUseThread = ability.can('create', 'Conversation');

  /** Set synchronously on the first tap, so a second tap in the same frame is ignored too. */
  const isOpeningRef = useRef(false);

  const { mutate, isPending } = useMutation<
    { conversationId: string },
    ConversationError,
    ContactSupportRequest
  >({
    mutationFn: () => getMySupportThread(),
    onSuccess: ({ conversationId }, { source, draft, inquiry }) => {
      if (draft || inquiry) {
        setSupportDraft({ conversationId, text: draft ?? '', inquiry });
      }

      trackSupportContactStarted({
        source,
        conversation_id: conversationId,
        has_draft: !!draft,
        has_inquiry: !!inquiry,
      });

      if (overTabs) {
        navigateIntoTabs(
          isCompactLayout
            ? `${ROUTES.INBOX}/chat/${conversationId}`
            : `${ROUTES.INBOX}?id=${encodeURIComponent(conversationId)}`
        );

        return;
      }

      router.push(
        isCompactLayout
          ? { pathname: ROUTES.INBOX_CHAT, params: { id: conversationId } }
          : { pathname: ROUTES.INBOX, params: { id: conversationId } }
      );
    },
    onError: (error) => {
      if (error.kind !== 'network') {
        if (onRefused) {
          onRefused();
        } else {
          void openEmailLink(SUPPORT_EMAIL_LINK);
        }

        return;
      }

      toast.showError({
        title: 'Couldn’t open Primum Support',
        message: 'Check your connection and try again.',
      });
    },
    onSettled: () => {
      isOpeningRef.current = false;
    },
  });

  const contact = useCallback(
    (request: ContactSupportRequest) => {
      if (isOpeningRef.current) {
        return;
      }

      isOpeningRef.current = true;

      mutate(request);
    },
    [mutate]
  );

  return { canUseThread, contact, isOpening: isPending };
}

/**
 * A "contact support" prompt that opens the member's support thread when they
 * may have one, and the support form otherwise. The caller renders the form
 * from `isFormOpen` and `closeForm` exactly as before; only the opening moves.
 */
export function useSupportPrompt(
  { source, draft }: ContactSupportRequest,
  options: UseContactSupportOptions = {}
) {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { canUseThread, contact, isOpening } = useContactSupport({
    ...options,
    onRefused: () => setIsFormOpen(true),
  });

  const open = useCallback(() => {
    if (canUseThread) {
      contact({ source, draft });

      return;
    }

    setIsFormOpen(true);
  }, [canUseThread, contact, source, draft]);

  const closeForm = useCallback(() => setIsFormOpen(false), []);

  return { open, isOpening, isFormOpen, closeForm };
}
